import { useWorkflowStore } from "../store/workflowStore";
import { NodeTypes, type NodeData, type RefName } from "../types/workflow";
import { useReactFlow } from "@xyflow/react";
import { useUIStore } from "../store/uiStore";
import { cn } from "@/utils/theme";
import { Icon, Input, ScrollArea } from "@elaniintc/goes-design-system";
import { NodeSearchOption } from "./node-search-option";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useOnClickOutside } from "usehooks-ts";

export const getNodeName = (type: RefName) => {
  switch (type) {
    case NodeTypes.INITIALIZER:
      return "Inicializador";
    case NodeTypes.FORM:
      return "Formulario";
    case NodeTypes.CORRECTION:
      return "Revisión";
    case NodeTypes.IF:
      return "Condicional";
    case NodeTypes.SWITCH:
      return "Switch";
    case NodeTypes.MULTITHREAD:
      return "Multihilo";
    case NodeTypes.WAIT:
      return "Espera";
    case NodeTypes.ASSIGN_GROUP:
      return "Asignar grupo";
    case NodeTypes.DATA_CENTER:
      return "Centro de datos";
    case NodeTypes.EXPEDIENTE:
      return "Expediente";
    case NodeTypes.NOTIFICATION:
      return "Notificación (Email/SMS)";
    case NodeTypes.CALCULATED:
      return "Campo calculado";
    case NodeTypes.PAYMENT:
      return "Pasarela de pago";
    case NodeTypes.N8N:
      return "Objeto N8N";
    case NodeTypes.INTEROPERABILITY:
      return "Interoperabilidad";
    case NodeTypes.DOC_GENERATOR:
      return "Generador de documentos";
    case NodeTypes.SIGN_DOCUMENT:
      return "Firma digital";
    case NodeTypes.RESOLUTION:
      return "Resolución de trámites";
    case NodeTypes.END:
      return "Objeto de finalización";
  }
};

const getIcon = (type: RefName) => {
  switch (type) {
    case NodeTypes.INITIALIZER:
      return { type: "fas", name: "play" };
    case NodeTypes.FORM: {
      return { type: "fas", name: "bars", className: "text-action" };
    }
    case NodeTypes.CORRECTION:
      return { type: "fas", name: "file-edit", className: "text-action" };
    case NodeTypes.IF:
      return { type: "fas", name: "toggle-on" };
    case NodeTypes.SWITCH:
      return { type: "fas", name: "arrows-cross" };
    case NodeTypes.MULTITHREAD:
      return { type: "fas", name: "code-branch" };
    case NodeTypes.WAIT:
      return { type: "fas", name: "hourglass-half" };
    case NodeTypes.ASSIGN_GROUP:
      return { type: "fas", name: "users", className: "text-info" };
    case NodeTypes.DATA_CENTER:
      return { type: "fas", name: "table", className: "text-info" };
    case NodeTypes.EXPEDIENTE:
      return { type: "fas", name: "folder-open", className: "text-info" };
    case NodeTypes.NOTIFICATION:
      return { type: "fas", name: "bell", className: "text-info" };
    case NodeTypes.CALCULATED:
      return { type: "fas", name: "function", className: "text-success" };
    case NodeTypes.PAYMENT:
      return { type: "fas", name: "dollar-sign", className: "text-warning" };
    case NodeTypes.N8N:
      return { type: "fas", name: "hexagon-nodes", className: "text-warning" };
    case NodeTypes.INTEROPERABILITY:
      return { type: "fas", name: "diagram-nested", className: "text-warning" };
    case NodeTypes.DOC_GENERATOR:
      return { type: "fas", name: "file-lines", className: "text-action" };
    case NodeTypes.SIGN_DOCUMENT:
      return { type: "fas", name: "signature", className: "text-action" };
    case NodeTypes.RESOLUTION:
      return { type: "fas", name: "files", className: "text-action" };
    case NodeTypes.END:
      return { type: "fas", name: "stop" };
  }
};

const NodeSearch = () => {
  const { nodes, getNodeById } = useWorkflowStore();
  const { setModalState, setSelectedNodeId } = useUIStore();
  const { setCenter } = useReactFlow();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      console.log(e.key === "Escape");
      if (e.key === "Escape") {
        e.preventDefault();
        setModalState("idle");
      }
    };
    document.addEventListener("keydown", down);
    return () => {
      document.removeEventListener("keydown", down);
    };
  }, [setModalState]);

  const [searchQuery, setSearchQuery] = useState("");
  const options = useMemo(() => {
    if (searchQuery !== "") {
      return (
        nodes
          ?.filter((n) => {
            const data = n.data as unknown as NodeData;
            return data.displayName
              .toLowerCase()
              .includes(searchQuery.toLowerCase());
          })
          .map((n) => {
            const data = n.data as unknown as NodeData;
            return {
              value: n.id,
              label: `${data.displayName} (${data.type})`,
              icon: getIcon(data.type as RefName),
            };
          }) || []
      );
    }
    return nodes?.map((n) => {
      const data = n.data as unknown as NodeData;
      return {
        value: n.id,
        label: `${getNodeName(data.type as RefName)} - ${data.displayName}`,
        icon: getIcon(data.type as RefName),
      };
    });
  }, [nodes, searchQuery]);

  return (
    <div className="absolute inset-0 bg-[#00000060] w-full h-full z-50 flex items-center justify-center">
      <Content
        options={options}
        search={{
          onSearch: (query) => setSearchQuery(query),
          placeholder: "Buscar nodo...",
          query: searchQuery,
        }}
        toggleSelection={({ value }) => {
          const node = getNodeById(value);
          if (!node) return;
          setSelectedNodeId(node.id);
          setCenter(node.position.x + 48, node.position.y + 96, {
            duration: 1000,
            ease: (t) =>
              t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
          });
          setModalState("idle");
        }}
        onClickOutside={() => {
          setModalState("idle");
        }}
      />
    </div>
  );
};

export default NodeSearch;

const Content = ({
  search,
  options,
  toggleSelection,
  onClickOutside,
}: {
  onClickOutside: (e: MouseEvent | TouchEvent | FocusEvent) => void;
  search: {
    onSearch: (query: string) => void;
    placeholder?: string;
    query: string;
  };
  options: Array<{
    value: string;
    label: string;
    icon?: { type: string; name: string };
  }>;
  toggleSelection: (item: { value: string; label: string }) => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const labelId = useId();
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  useOnClickOutside(ref as React.RefObject<HTMLElement>, onClickOutside);

  // Auto-focus input when modal opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Reset active index when options change
  useEffect(() => {
    setActiveIndex(options.length > 0 ? 0 : -1);
  }, [options]);

  // Scroll active option into view
  useEffect(() => {
    if (activeIndex >= 0) {
      const activeElement = document.getElementById(
        `option-${listboxId}-${activeIndex}`,
      );
      activeElement?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, listboxId]);

  // Focus trap and keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      console.log(e.key);
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex((prev) => (prev < options.length - 1 ? prev + 1 : 0));
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : options.length - 1));
          break;
        case "Enter":
          e.preventDefault();
          if (activeIndex >= 0 && options[activeIndex]) {
            toggleSelection(options[activeIndex]);
          }
          break;
        case "Home":
          e.preventDefault();
          setActiveIndex(0);
          break;
        case "End":
          e.preventDefault();
          setActiveIndex(options.length - 1);
          break;
        case "PageUp":
          e.preventDefault();
          setActiveIndex((prev) => Math.max(prev - 5, 0));
          break;
        case "PageDown":
          e.preventDefault();
          setActiveIndex((prev) => Math.min(prev + 5, options.length - 1));
          break;
        case "Tab":
          // Trap focus within modal
          e.preventDefault();
          inputRef.current?.focus();
          break;
      }
    },
    [activeIndex, options, toggleSelection],
  );

  const activeOptionId =
    activeIndex >= 0 ? `option-${listboxId}-${activeIndex}` : undefined;

  return (
    <div
      className={cn(
        "w-1/2 flex flex-col rounded-lg border-[0.5px] border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl",
      )}
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelId}
      onKeyDown={handleKeyDown}
    >
      <h2 id={labelId} className="sr-only">
        Buscar nodo
      </h2>
      <div className="border-b border-neutral-quiet p-3 relative ">
        <Icon
          type="far"
          name="search"
          className="absolute left-5 top-1/2 transform -translate-y-1/2 text-body"
          aria-hidden="true"
        />
        <Input
          ref={inputRef}
          variant="sm"
          className="text-sm w-full font-normal flex-auto pl-9"
          placeholder={search.placeholder || "Buscar..."}
          onChange={(e) => search.onSearch(e.target.value)}
          value={search.query}
          role="combobox"
          aria-label="Buscar nodo"
          aria-expanded="true"
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          aria-autocomplete="list"
        />
      </div>
      {/* Live region for screen reader announcements */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {options.length === 0
          ? "No se encontraron coincidencias"
          : `${options.length} resultado${options.length !== 1 ? "s" : ""} encontrado${options.length !== 1 ? "s" : ""}`}
      </div>
      <ScrollArea className={cn("h-[264px] p-2")}>
        <div role="listbox" id={listboxId} aria-label="Resultados de búsqueda">
          {options.length > 0 ? (
            options.map((item, index) => (
              <NodeSearchOption
                key={item.value}
                id={`option-${listboxId}-${index}`}
                value={item.value}
                label={item.label}
                icon={item.icon}
                isActive={index === activeIndex}
                onSelect={toggleSelection}
                onMouseEnter={() => setActiveIndex(index)}
              />
            ))
          ) : (
            <div
              className="flex items-center justify-center py-5"
              role="status"
            >
              <span className="text-body-quiet">
                No se encontraron coincidencias
              </span>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
